/**
 * Inline alert for a form- or section-level error. Nine of the ten call sites
 * in the app want exactly this one.
 *
 * `role="alert"` with `aria-live="assertive"` because an error is the one thing
 * worth interrupting a screen reader for — it appears in response to something
 * the user just did, and it is usually the reason the thing they did did not
 * work.
 *
 * `onDismiss` is optional on purpose: a banner explaining why a submit failed
 * should stay until the next attempt, while one reporting a background refresh
 * failure should be closable.
 */
export interface ErrorBannerProps {
  message: string;
  onDismiss?: () => void;
}

export function ErrorBanner({ message, onDismiss }: ErrorBannerProps) {
  return (
    <div role="alert" className="error-banner" aria-live="assertive">
      <span className="error-banner__icon" aria-hidden="true">⚠</span>
      <span className="error-banner__text">{message}</span>
      {onDismiss && (
        <button
          type="button"
          className="error-banner__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss error"
        >
          ✕
        </button>
      )}
    </div>
  );
}
